// Session-scoped insurance plan for the cashless Auto workflow.
// Captured on the first stepper step; later NHCX screens read policy no / payer.

import {
  NHCX_DUMMY_PAYER_CODE,
  NHCX_INSURER_REGISTRY,
  NHCX_SANDBOX_POLICY_NO,
} from "./nhcx-client";

const PAYERS = [
  "Star Health",
  "ICICI Lombard",
  "HDFC Ergo",
  "Bajaj Allianz",
  "New India Assurance",
  "MediAssist TPA",
  "Paramount TPA",
  "FHPL TPA",
  "Vidal Health TPA",
];

const PAYER_TYPE_LABELS = new Set([
  "insurance",
  "tpa",
  "cash",
  "self",
  "self pay",
  "corp",
  "corporate",
  "staff",
  "government",
  "government scheme",
  "govt scheme",
]);

export const INSURANCE_COMPANIES = [
  ...NHCX_INSURER_REGISTRY.map((i) => ({ key: i.key, label: i.label })),
  ...PAYERS.map((name) => ({ key: name, label: name })),
];

export const EMPTY_INSURANCE_PLAN = {
  payerCode: "",
  policyNo: "",
  description: "",
  // Derived from payerCode, not entered. Eligibility / Pre-Auth pick their insurer
  // (rohini id, phone, email, namespace URI) off this key.
  insurerKey: "",
  submitted: false,
};

function storageKey(admissionId) {
  return `ipd-nhcx-insurance-plan:${admissionId}`;
}

export function readInsurancePlan(admissionId) {
  if (!admissionId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(admissionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return { ...EMPTY_INSURANCE_PLAN, ...parsed };
  } catch {
    return null;
  }
}

export function writeInsurancePlan(admissionId, plan) {
  if (!admissionId) return;
  try {
    sessionStorage.setItem(storageKey(admissionId), JSON.stringify(plan));
  } catch {
    // private mode
  }
}

/** Strip "Star Health (Insurance)" → "Star Health". */
function stripPayerTypeSuffix(label) {
  return String(label || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
}

/** The named insurer/TPA on this admission — not the payer-type chip. */
export function patientPayerName(display, patient) {
  const raw =
    String(display?.provider || "").trim() ||
    String(display?.tpa || "").trim() ||
    stripPayerTypeSuffix(patient?.payer);
  if (!raw) return "";
  if (PAYER_TYPE_LABELS.has(raw.toLowerCase())) return "";
  return raw;
}

export function matchInsuranceCompany(name) {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return null;
  const exact = INSURANCE_COMPANIES.find(
    (c) => c.key.toLowerCase() === n || c.label.toLowerCase() === n
  );
  if (exact) return exact;
  if (n === "dummy payer" || n.includes("dummy payer")) {
    return INSURANCE_COMPANIES.find((c) => c.key === "dummy-sandbox") || null;
  }
  return (
    INSURANCE_COMPANIES.find((c) => {
      const label = c.label.toLowerCase();
      return label.includes(n) || n.includes(label);
    }) || null
  );
}

/**
 * The registry insurer that answers to this payer code, or "" when the code is not
 * one we hold NHCX contact details for. Downstream panels fall back to their own
 * default insurer on "".
 */
export function insurerKeyForPayerCode(payerCode) {
  const code = String(payerCode || "").trim().toLowerCase();
  if (!code) return "";
  return (
    NHCX_INSURER_REGISTRY.find(
      (i) => String(i.payer_code).toLowerCase() === code
    )?.key || ""
  );
}

export function seedInsurancePlan(display, patient) {
  const match = matchInsuranceCompany(patientPayerName(display, patient));
  const entry = NHCX_INSURER_REGISTRY.find((i) => i.key === match?.key);
  const payerCode = entry?.payer_code || "";
  const isSandbox = payerCode === NHCX_DUMMY_PAYER_CODE;
  return {
    payerCode,
    policyNo: String(display?.policy || "").trim() || (isSandbox ? NHCX_SANDBOX_POLICY_NO : ""),
    description: "",
    insurerKey: entry?.key || "",
    submitted: false,
  };
}
